import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import BtButton from '../bt-button/bt-button';
import Loading from '../../components/shared/Loading';
import styles from './page-updates.module.scss';
import { Newsletter } from '../../services/newsletterService';
import { asReaderDocument } from './readerDocument';
import { formatSentAt } from './NewsletterRail';

type Props = {
  newsletter?: Newsletter,
  /** null while the letter is still loading. */
  html: string | null,
  failed: boolean,
  onRetry: () => void,
}

const NewsletterReader: FC<Props> = ({ newsletter, html, failed, onRetry }) => {
  const [t, i18n] = useTranslation('updates');

  if (!newsletter) return null;

  return (
    <div className={styles.reader}>
      <div className={styles.readerHeader}>
        <div>
          <h2 className={styles.readerTitle}>{newsletter.title}</h2>
          <div className={styles.readerMeta}>
            {t('sentAt', {
              date: formatSentAt(newsletter.sentAt, i18n.language),
              defaultValue: 'Skickat {{date}}',
            })}
          </div>
        </div>
        {newsletter.archiveUrl && (
          <a href={newsletter.archiveUrl} target="_blank" rel="noreferrer">
            <BtButton color="white" size="small">
              {t('openInNewWindow', 'Öppna i nytt fönster')}
            </BtButton>
          </a>
        )}
      </div>

      <div className={styles.readerBody} aria-live="polite">
        {failed ? (
          <div className={styles.readerError}>
            <p>{t('error.content', 'Nyhetsbrevet kunde inte hämtas just nu.')}</p>
            <BtButton color="white" size="small" onClick={onRetry}>
              {t('error.retry', 'Försök igen')}
            </BtButton>
          </div>
        ) : html === null ? (
          <div className={styles.readerLoading}><Loading type="inline" /></div>
        ) : (
          // The letter is our own content, but it is still third-party markup
          // rendered inside the app: no allow-scripts, so nothing in it runs.
          <iframe
            className={styles.readerFrame}
            title={newsletter.title}
            srcDoc={asReaderDocument(html)}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
          />
        )}
      </div>
    </div>
  );
};

export default NewsletterReader;
